import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { ArrowLeft, Edit2, ExternalLink, QrCode, Trash2 } from 'lucide-react'

interface Listing {
  id: string
  assigned_code: string
  address_street: string
  address_city: string
  address_state: string
  address_zip: string
  price: number
  beds: number
  baths: number
  sqft: number
  description: string
  status: string
  photos: { url: string; order: number }[]
  listing_agent_name: string
  listing_agent_phone: string
  listing_agent_email: string
}

export default function ListingDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [listing, setListing] = useState<Listing | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editedListing, setEditedListing] = useState<Partial<Listing>>({})

  useEffect(() => {
    fetchListing()
  }, [id])

  const fetchListing = async () => {
    const { data, error } = await supabase
      .from('listings')
      .select('*')
      .eq('id', id)
      .single()

    if (!error && data) {
      setListing(data)
      setEditedListing(data)
    }
    setLoading(false)
  }

  const handleSave = async () => {
    const { error } = await supabase
      .from('listings')
      .update(editedListing)
      .eq('id', id)

    if (!error) {
      setListing({ ...listing!, ...editedListing })
      setIsEditing(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Delete this listing?')) return
    
    await supabase.from('listings').delete().eq('id', id)
    navigate('/listings')
  }

  if (loading) return <div className="p-8">Loading...</div>
  if (!listing) return <div className="p-8">Listing not found</div>

  const publicUrl = `https://insidethishome.com/${listing.assigned_code}`
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(publicUrl)}`

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/listings" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft size={24} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{listing.address_street}</h1>
            <p className="text-gray-500">{listing.address_city}, {listing.address_state} {listing.address_zip}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ExternalLink size={18} />
            View Public
          </a>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <Edit2 size={18} />
            {isEditing ? 'Cancel' : 'Edit'}
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Photos */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Photos</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {listing.photos?.map((photo, idx) => (
                <div key={idx} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  <img src={photo.url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
              {!listing.photos?.length && (
                <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                  No photos
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Property Details</h2>
            
            {isEditing ? (
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={editedListing.address_street || ''}
                  onChange={(e) => setEditedListing({ ...editedListing, address_street: e.target.value })}
                  placeholder="Street Address"
                  className="px-3 py-2 border rounded-md"
                />
                <input
                  type="number"
                  value={editedListing.price || ''}
                  onChange={(e) => setEditedListing({ ...editedListing, price: parseInt(e.target.value) })}
                  placeholder="Price"
                  className="px-3 py-2 border rounded-md"
                />
                <input
                  type="number"
                  step="0.5"
                  value={editedListing.beds || ''}
                  onChange={(e) => setEditedListing({ ...editedListing, beds: parseFloat(e.target.value) })}
                  placeholder="Beds"
                  className="px-3 py-2 border rounded-md"
                />
                <input
                  type="number"
                  step="0.5"
                  value={editedListing.baths || ''}
                  onChange={(e) => setEditedListing({ ...editedListing, baths: parseFloat(e.target.value) })}
                  placeholder="Baths"
                  className="px-3 py-2 border rounded-md"
                />
                <textarea
                  value={editedListing.description || ''}
                  onChange={(e) => setEditedListing({ ...editedListing, description: e.target.value })}
                  placeholder="Description"
                  rows={4}
                  className="md:col-span-2 px-3 py-2 border rounded-md"
                />
                <button
                  onClick={handleSave}
                  className="md:col-span-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                >
                  Save Changes
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500">Price</span>
                  <p className="text-xl font-semibold">${listing.price?.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Status</span>
                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                    listing.status === 'active' ? 'bg-green-100 text-green-800' :
                    listing.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {listing.status}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Beds</span>
                  <p className="font-medium">{listing.beds}</p>
                </div>
                <div>
                  <span className="text-gray-500">Baths</span>
                  <p className="font-medium">{listing.baths}</p>
                </div>
                <div>
                  <span className="text-gray-500">Sqft</span>
                  <p className="font-medium">{listing.sqft?.toLocaleString()}</p>
                </div>
                <div className="md:col-span-2">
                  <span className="text-gray-500">Description</span>
                  <p className="mt-1">{listing.description || 'No description'}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* QR Code */}
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <h2 className="text-lg font-semibold mb-4 flex items-center justify-center gap-2">
              <QrCode size={20} />
              QR Code
            </h2>
            <div className="bg-white p-4 rounded-lg inline-block">
              <img src={qrUrl} alt="QR Code" className="w-48 h-48" />
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Code: <span className="font-mono font-medium">{listing.assigned_code}</span>
            </p>
            <a
              href={qrUrl}
              download={`qr-${listing.assigned_code}.png`}
              className="mt-4 inline-block px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Download QR
            </a>
          </div>

          {/* Agent Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Listing Agent</h2>
            <p className="font-medium">{listing.listing_agent_name}</p>
            <p className="text-gray-500 text-sm">{listing.listing_agent_phone}</p>
            <p className="text-gray-500 text-sm">{listing.listing_agent_email}</p>
          </div>

          {/* Public URL */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Public URL</h2>
            <input
              type="text"
              value={publicUrl}
              readOnly
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
            <button
              onClick={() => navigator.clipboard.writeText(publicUrl)}
              className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Copy Link
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
